import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BellRing,
  Check,
  ChevronDown,
  DoorOpen,
  Languages,
  LayoutDashboard,
  QrCode,
  ShieldCheck,
  BadgeCheck,
  Smartphone,
  Star,
  Send,
  CirclePlay,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { applyDocLang, Brand, GemStar, LangToggle, LogoMark, saveLang } from "@/components/brand";
import { landingCopy } from "@/lib/landingCopy";
import { CONTACT_EMAIL, PRICE } from "@/lib/site";
import PromoVideo from "@/components/PromoVideo";
import { SEO, useSeo } from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ease = [0.22, 1, 0.36, 1];
const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease },
};

/* Decorative QR pattern — deterministic so it renders the same every time. */
const QrArt = ({ size = 108 }) => {
  const n = 21;
  const cells = [];
  let seed = 7;
  const rnd = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  const finder = (x, y) =>
    (x < 7 && y < 7) || (x >= n - 7 && y < 7) || (x < 7 && y >= n - 7);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (finder(x, y)) continue;
      if (rnd() > 0.52) cells.push(<rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" />);
    }
  }
  const Finder = ({ x, y }) => (
    <g transform={`translate(${x} ${y})`}>
      <rect width="7" height="7" rx="1.6" />
      <rect x="1" y="1" width="5" height="5" rx="1.1" fill="#f3efff" />
      <rect x="2" y="2" width="3" height="3" rx="0.7" />
    </g>
  );
  return (
    <svg width={size} height={size} viewBox={`0 0 ${n} ${n}`} fill="#1a1230" shapeRendering="crispEdges" aria-hidden="true">
      {cells}
      <Finder x={0} y={0} />
      <Finder x={n - 7} y={0} />
      <Finder x={0} y={n - 7} />
    </svg>
  );
};

const T3 = ({ parts }) => (
  <>
    {parts[0]}
    <em>{parts[1]}</em>
    {parts[2] || ""}
  </>
);

const HeroStage = ({ m }) => (
  <div className="stage-3d relative mx-auto" style={{ width: "min(460px, 100%)", height: 600 }}>
    <div className="orb" style={{ width: 340, height: 340, left: "50%", top: 120, marginLeft: -170, background: "rgba(139,92,246,.55)" }} />

    <div className="absolute" style={{ left: "50%", top: 20, transform: "translateX(-50%)" }}>
      <div className="device">
        <div className="device-screen">
          <div className="device-notch" />
          <div className="flex flex-col items-center text-center" style={{ padding: "64px 18px 0" }}>
            <div className="step-token" style={{ width: 46, height: 46, fontSize: "1.4rem", borderRadius: 14 }}>{m.monogram}</div>
            <div className="eyebrow mt-4" style={{ fontSize: 9 }}>{m.welcome}</div>
            <div className="display" style={{ fontSize: 20 }}>{m.hotel}</div>
            <div className="display mt-6" style={{ fontSize: 22, lineHeight: 1.15 }}>{m.q}</div>
            <div className="flex gap-1 mt-5">
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n} style={{ width: 38, height: 38 }} className={n <= 2 ? "star-btn is-lit" : "star-btn"}>
                  <GemStar lit={n <= 2} />
                </div>
              ))}
            </div>
            <div className="rating-word mt-2" style={{ fontSize: 9 }}>{m.word}</div>
            <div className="field mt-4 text-left" style={{ minHeight: 76, fontSize: 12, color: "var(--text-2)", padding: "10px 12px" }}>{m.msg}</div>
            <div className="btn btn-primary btn-block mt-4" style={{ minHeight: 40, fontSize: 13 }}>
              <Send size={14} /> {m.send}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="float-card" style={{ right: -10, top: -8 }}>
      <div className="panel" style={{ padding: "12px 14px", borderRadius: 16, width: 214 }}>
        <span className="pulse-ring" />
        <div className="flex items-center gap-2">
          <span className="chip chip-danger chip-dot" style={{ padding: "2px 8px", fontSize: 10 }}>{m.live}</span>
          <span className="mono faint" style={{ fontSize: 10 }}>{m.now}</span>
        </div>
        <div className="mt-2 font-bold text-sm">{m.room} 305 · 2★</div>
        <div className="muted" style={{ fontSize: 12, lineHeight: 1.4 }}>{m.msg}</div>
      </div>
    </div>

    <div className="float-card delay-1" style={{ left: -4, bottom: 70 }}>
      <div className="qr-tent">
        <div className="flex justify-center"><QrArt size={104} /></div>
        <div className="text-center mt-2" style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".08em" }}>{m.qr}</div>
      </div>
    </div>

    <div className="float-card delay-2" style={{ right: 4, bottom: 40 }}>
      <div className="panel flex items-center gap-3" style={{ padding: "10px 14px", borderRadius: 16 }}>
        <div className="grid place-items-center" style={{ width: 34, height: 34, borderRadius: 10, background: "#fff" }}>
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#4285F4" d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" />
          </svg>
        </div>
        <div>
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <span key={n} style={{ width: 13, height: 13, display: "inline-block" }}><GemStar lit /></span>
            ))}
          </div>
          <div className="faint" style={{ fontSize: 11 }}>{m.google}</div>
        </div>
      </div>
    </div>
  </div>
);

const STEP_ICONS = [QrCode, Star, BellRing, ShieldCheck];
const FEATURE_ICONS = [BellRing, DoorOpen, Languages, LayoutDashboard, Smartphone, ShieldCheck];

const DemoForm = ({ d, lang }) => {
  const [form, setForm] = useState({ name: "", hotel_name: "", city: "", rooms: "", phone: "", email: "", message: "", consent: false, website: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone && !form.email) return toast.error(d.errContact);
    if (!form.consent) return toast.error(d.errConsent);
    setSending(true);
    try {
      await axios.post(`${API}/demo-requests`, { ...form, message: `[${lang}] ${form.message}`.trim() });
      setDone(true);
    } catch (err) {
      toast.error(d.errSend);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="demo-success">
        <div className="step-token mx-auto mb-5" style={{ background: "linear-gradient(160deg,#6ee7a8,#15803d)", boxShadow: "inset 0 1px 0 rgba(255,255,255,.5), 0 4px 0 #0b4d26, 0 16px 30px -10px rgba(74,222,128,.6)" }}>
          <Check size={26} strokeWidth={3} />
        </div>
        <h3 className="display" style={{ fontSize: "2rem" }}>{d.okTitle}</h3>
        <p className="muted mt-2">{d.okText}</p>
      </div>
    );
  }

  const f = d.f;
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="demo-form">
      <div><label className="field-label" htmlFor="d-name">{f.name}</label><input id="d-name" className="field" required minLength={2} value={form.name} onChange={set("name")} autoComplete="name" /></div>
      <div><label className="field-label" htmlFor="d-hotel">{f.hotel}</label><input id="d-hotel" className="field" required minLength={2} value={form.hotel_name} onChange={set("hotel_name")} autoComplete="organization" /></div>
      <div><label className="field-label" htmlFor="d-city">{f.city}</label><input id="d-city" className="field" value={form.city} onChange={set("city")} placeholder={f.cityPh} /></div>
      <div><label className="field-label" htmlFor="d-rooms">{f.rooms}</label><input id="d-rooms" className="field" inputMode="numeric" value={form.rooms} onChange={set("rooms")} placeholder={f.roomsPh} /></div>
      <div><label className="field-label" htmlFor="d-phone">{f.phone}</label><input id="d-phone" className="field" type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" placeholder={f.phonePh} /></div>
      <div><label className="field-label" htmlFor="d-email">{f.email}</label><input id="d-email" className="field" type="email" value={form.email} onChange={set("email")} autoComplete="email" /></div>
      <div className="sm:col-span-2"><label className="field-label" htmlFor="d-msg">{f.note}</label><textarea id="d-msg" className="field" rows={3} value={form.message} onChange={set("message")} placeholder={f.notePh} style={{ minHeight: 96 }} /></div>
      <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
      <label className="check sm:col-span-2">
        <input type="checkbox" checked={form.consent} onChange={set("consent")} />
        <span>
          {d.consent} <Link to={`/gizlilik?lang=${lang}`} className="underline underline-offset-4">{d.privacy}</Link>
        </span>
      </label>
      <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
        <button type="submit" className="btn btn-primary" disabled={sending} data-testid="demo-submit">
          {sending ? d.sending : d.submit} <ArrowRight size={18} />
        </button>
        <span className="faint text-sm">{d.reply}</span>
      </div>
    </form>
  );
};

const LANG_PATH = { tr: "/", en: "/en", sr: "/sr" };

export const Landing = ({ lang: forced }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(0);
  /* "/" is the Turkish page for search engines; a visitor's saved choice still wins. */
  const lang = forced || (() => {
    try {
      const saved = localStorage.getItem("rg-lang");
      if (saved === "en" || saved === "sr" || saved === "tr") return saved;
    } catch (e) {
      /* storage unavailable */
    }
    return "tr";
  })();
  const c = landingCopy[lang] || landingCopy.tr;
  const price = PRICE[lang] || PRICE.tr;

  useEffect(() => applyDocLang(lang), [lang]);
  useSeo(SEO[lang] || SEO.tr);

  /* Links like rateguardnow.com/#video or /#demo arrive before the section renders. */
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return undefined;
    const timer = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
    return () => clearTimeout(timer);
  }, []);

  const switchLang = (code) => {
    saveLang(code);
    navigate(LANG_PATH[code]);
  };

  const h2 = { fontSize: "clamp(2.2rem, 5vw, 3.4rem)" };

  return (
    <div className="scene">
      <header
        className="sticky z-40"
        style={{ top: "env(safe-area-inset-top, 0px)", background: "linear-gradient(180deg, rgba(10,8,18,.85), rgba(10,8,18,.55))", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", borderBottom: "1px solid var(--line)" }}
      >
        <div className="wrap flex items-center justify-between gap-3" style={{ height: 68 }}>
          <Brand to={LANG_PATH[lang]} />
          <nav className="hidden lg:flex items-center gap-1">
            <a href="#how" className="btn btn-ghost">{c.nav.how}</a>
            <a href="#features" className="btn btn-ghost">{c.nav.features}</a>
            <a href="#pricing" className="btn btn-ghost">{c.nav.pricing}</a>
            <a href="#faq" className="btn btn-ghost">{c.nav.faq}</a>
          </nav>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex"><LangToggle lang={lang} onChange={switchLang} testId="landing-language" /></span>
            <Link to="/admin" className="btn btn-ghost hidden md:inline-flex">{c.nav.login}</Link>
            <a href="#demo" className="btn btn-primary btn-sm">{c.nav.demo}</a>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="floor" />
        <div className="wrap grid lg:grid-cols-[1.05fr_1fr] gap-10 items-center" style={{ paddingBlock: "clamp(3rem, 8vw, 6rem)" }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }} className="min-w-0">
            <div className="sm:hidden mb-5"><LangToggle lang={lang} onChange={switchLang} testId="landing-language-m" /></div>
            <span className="chip chip-violet mb-6"><LogoMark size={16} /> {c.hero.chip}</span>
            <h1 className="display" style={{ fontSize: "clamp(2.5rem, 6.2vw, 4.4rem)" }}><T3 parts={c.hero.title} /></h1>
            <p className="lede mt-6">{c.hero.lede}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#demo" className="btn btn-primary" data-testid="hero-demo">{c.hero.cta} <ArrowRight size={18} /></a>
              <Link to="/demo/101" className="btn btn-secondary" data-testid="hero-try"><CirclePlay size={18} /> {c.hero.try}</Link>
            </div>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 mt-8 muted text-sm" style={{ listStyle: "none", padding: 0 }}>
              {c.hero.ticks.map((x) => (
                <li key={x} className="flex items-center gap-2"><Check size={16} className="text-[var(--ok)]" /> {x}</li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease, delay: 0.15 }} className="min-w-0">
            <HeroStage m={c.mock} />
          </motion.div>
        </div>
      </section>

      {lang === "tr" && (
        <section id="video" className="wrap text-center" style={{ paddingBlock: "clamp(2rem, 5vw, 3.5rem) 1rem", scrollMarginTop: 80 }}>
          <motion.div {...reveal}>
            <div className="eyebrow mb-3">45 saniyede RateGuard</div>
            <h2 className="display mx-auto mb-8" style={{ fontSize: "clamp(1.8rem, 4.2vw, 2.8rem)", maxWidth: "22ch" }}>
              Sistemin nasıl çalıştığını <em>izleyin.</em>
            </h2>
            <PromoVideo testId="landing-video" />
          </motion.div>
        </section>
      )}

      <section className="wrap" style={{ paddingBlock: "clamp(3rem, 7vw, 5rem)" }}>
        <motion.div {...reveal} className="panel panel-rim grid md:grid-cols-[1fr_1.2fr] gap-8 items-center" style={{ padding: "clamp(1.6rem, 4vw, 3rem)" }}>
          <div>
            <div className="eyebrow mb-3">{c.problem.eyebrow}</div>
            <h2 className="display" style={{ fontSize: "clamp(2rem, 4.6vw, 3.1rem)" }}><T3 parts={c.problem.title} /></h2>
          </div>
          <div className="grid gap-4">
            {c.problem.items.map(([h, p]) => (
              <div key={h} className="panel-flat flex gap-4" style={{ padding: "1rem 1.1rem" }}>
                <span className="chip chip-danger chip-dot" style={{ alignSelf: "flex-start", padding: "4px 6px" }} />
                <div><div className="font-bold">{h}</div><div className="muted text-sm">{p}</div></div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      <section id="how" className="wrap" style={{ paddingBlock: "clamp(3rem, 7vw, 5rem)", scrollMarginTop: 80 }}>
        <motion.div {...reveal} className="text-center mb-12">
          <div className="eyebrow mb-3">{c.how.eyebrow}</div>
          <h2 className="display mx-auto" style={{ ...h2, maxWidth: "18ch" }}><T3 parts={c.how.title} /></h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {c.how.steps.map(([title, text], i) => {
            const Icon = STEP_ICONS[i];
            return (
              <motion.div key={title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }} className="panel" style={{ padding: "1.6rem 1.4rem" }}>
                <div className="flex items-center justify-between mb-6">
                  <div className="step-token">{String(i + 1).padStart(2, "0")}</div>
                  <Icon size={22} className="text-[var(--violet-300)]" />
                </div>
                <h3 className="text-lg font-bold mb-2">{title}</h3>
                <p className="muted text-[0.95rem]">{text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section id="features" className="wrap" style={{ paddingBlock: "clamp(3rem, 7vw, 5rem)", scrollMarginTop: 80 }}>
        <motion.div {...reveal} className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
          <div className="lg:sticky" style={{ top: 110 }}>
            <div className="eyebrow mb-3">{c.features.eyebrow}</div>
            <h2 className="display" style={h2}><T3 parts={c.features.title} /></h2>
            <p className="lede mt-5">{c.features.lede}</p>
            <a href="#demo" className="btn btn-secondary mt-8">{c.features.cta} <ArrowRight size={18} /></a>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {c.features.items.map(([title, text], i) => {
              const Icon = FEATURE_ICONS[i];
              return (
                <div key={title} className="panel-flat" style={{ padding: "1.3rem" }}>
                  <div className="stat-icon" style={{ marginBottom: "0.9rem" }}><Icon size={18} /></div>
                  <div className="font-bold">{title}</div>
                  <div className="muted text-sm mt-1">{text}</div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </section>

      <section className="wrap" style={{ paddingBlock: "1rem 3rem" }}>
        <motion.div {...reveal} className="panel-flat flex flex-col md:flex-row gap-5 items-start md:items-center" style={{ padding: "1.4rem 1.6rem", borderColor: "rgba(74,222,128,.25)", background: "rgba(74,222,128,.04)" }}>
          <ShieldCheck size={30} className="text-[var(--ok)] shrink-0" />
          <p className="muted" style={{ margin: 0 }}><strong className="text-[var(--text)]">{c.policy[0]}</strong>{c.policy[1]}</p>
        </motion.div>
      </section>

      <section id="pricing" className="wrap" style={{ paddingBlock: "clamp(3rem, 7vw, 5rem)", scrollMarginTop: 80 }}>
        <motion.div {...reveal} className="text-center mb-12">
          <div className="eyebrow mb-3">{c.pricing.eyebrow}</div>
          <h2 className="display" style={h2}><T3 parts={c.pricing.title} /></h2>
        </motion.div>
        <motion.div {...reveal} className="panel panel-rim mx-auto grid md:grid-cols-[0.9fr_1.1fr] gap-8 items-center" style={{ maxWidth: 880, padding: "clamp(1.6rem, 4vw, 2.6rem)" }} data-testid="pricing-card">
          <div>
            <span className="chip chip-violet">{c.pricing.chip}</span>
            <div className="mt-5 flex items-baseline gap-2 flex-wrap">
              <span className="display" style={{ fontSize: "clamp(3.6rem, 9vw, 5rem)", color: "var(--star)", lineHeight: 1 }}>{price.main}</span>
              <span className="display" style={{ fontSize: "1.6rem", color: "var(--text-2)" }}>{price.unit}</span>
            </div>
            <div className="faint text-sm mt-2">{price.note}</div>
            <div className="grid gap-2 mt-6">
              {c.pricing.badges.map((b) => (
                <span key={b} className="chip chip-resolved" style={{ justifySelf: "start", whiteSpace: "normal" }}><BadgeCheck size={14} /> {b}</span>
              ))}
            </div>
          </div>
          <div>
            <ul className="grid gap-3 muted" style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {c.pricing.bullets.map((x) => (
                <li key={x} className="flex gap-3"><Check size={18} className="text-[var(--ok)] shrink-0 mt-0.5" />{x}</li>
              ))}
            </ul>
            <a href="#demo" className="btn btn-primary btn-block mt-8">{c.pricing.cta} <ArrowRight size={18} /></a>
          </div>
        </motion.div>
      </section>

      <section id="faq" className="wrap" style={{ paddingBlock: "clamp(3rem, 7vw, 5rem)", scrollMarginTop: 80, maxWidth: 860 }}>
        <motion.div {...reveal} className="text-center mb-10">
          <div className="eyebrow mb-3">{c.faq.eyebrow}</div>
          <h2 className="display" style={{ fontSize: "clamp(2.2rem, 5vw, 3.2rem)" }}>{c.faq.title}</h2>
        </motion.div>
        <div className="grid gap-3">
          {c.faq.items.map(([q, a], i) => (
            <div key={q} className="panel-flat" style={{ padding: 0 }}>
              <button
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                aria-expanded={open === i}
                className="w-full flex items-center justify-between gap-4 text-left"
                style={{ padding: "1.15rem 1.3rem", background: "transparent", border: 0, color: "var(--text)", cursor: "pointer", fontWeight: 700, fontSize: "1rem", fontFamily: "inherit" }}
              >
                {q}
                <ChevronDown size={20} className="shrink-0 transition-transform" style={{ transform: open === i ? "rotate(180deg)" : "none", color: "var(--violet-300)" }} />
              </button>
              {open === i && <p className="muted" style={{ padding: "0 1.3rem 1.2rem", margin: 0 }}>{a}</p>}
            </div>
          ))}
        </div>
      </section>

      <section id="demo" className="wrap" style={{ paddingBlock: "clamp(3rem, 7vw, 5rem)", scrollMarginTop: 80 }}>
        <motion.div {...reveal} className="panel panel-rim grid lg:grid-cols-[0.8fr_1.2fr] gap-10" style={{ padding: "clamp(1.6rem, 4vw, 3rem)" }}>
          <div>
            <div className="eyebrow mb-3">{c.demo.eyebrow}</div>
            <h2 className="display" style={{ fontSize: "clamp(2.2rem, 5vw, 3.2rem)" }}><T3 parts={c.demo.title} /></h2>
            <p className="lede mt-5">{c.demo.lede}</p>
            <div className="mt-8 grid gap-3 muted text-sm">
              {c.demo.ticks.map((t) => (
                <div key={t} className="flex gap-3"><Check size={18} className="text-[var(--ok)]" /> {t}</div>
              ))}
              {CONTACT_EMAIL && <a href={`mailto:${CONTACT_EMAIL}`} className="flex gap-3 mono hover:text-[var(--text)]" data-testid="contact-email"><Send size={16} className="text-[var(--violet-300)]" /> {CONTACT_EMAIL}</a>}
            </div>
          </div>
          <div className="min-w-0"><DemoForm d={c.demo} lang={lang} /></div>
        </motion.div>
      </section>

      <footer style={{ borderTop: "1px solid var(--line)" }}>
        <div className="wrap flex flex-col sm:flex-row gap-4 items-center justify-between" style={{ paddingBlock: "2rem" }}>
          <Brand to={LANG_PATH[lang]} size={28} />
          <div className="flex flex-wrap items-center justify-center gap-5 text-sm faint">
            <Link to="/admin" className="hover:text-[var(--text)]">{c.footer.login}</Link>
            <Link to={`/gizlilik?lang=${lang}`} className="hover:text-[var(--text)]">{c.footer.privacy}</Link>
            {CONTACT_EMAIL ? <a href={`mailto:${CONTACT_EMAIL}`} className="mono hover:text-[var(--text)]">{CONTACT_EMAIL}</a> : <a href="#demo" className="hover:text-[var(--text)]">{c.footer.contact}</a>}
            <span>© {new Date().getFullYear()} RateGuard</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
